import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import { toast } from '@/components';
import { getExportJobCacheKey } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { ExportJob, ExportJobStatus, ExportType } from '@/types/export';
import { triggerBlobDownload } from '@/utils/download';
import { getApiErrorMessage } from '@/utils/error-messages';
import { resolveDownloadFileName } from '@/utils/file-name';

const POLL_INTERVAL_MS = 2_000;
const MAX_POLL_ERRORS = 5;

type ExportJobPhase = 'idle' | 'starting' | 'processing' | 'downloading';

interface ExportJobMessages {
    starting: string;
    startingWithCount?: (count: number) => string;
    progress: (progress: number) => string;
    ready: string;
    readySkipped: (skippedCount: number) => string;
    failed: string;
}

interface ActiveExportJob {
    id: string;
    fileName: string;
}

interface UseExportJobParams {
    toastId: string;
    messages: ExportJobMessages;
}

interface UseExportJobResult {
    busy: boolean;
    start: (
        type: ExportType,
        data: Record<string, unknown>,
        fileName: string,
        itemCount?: number,
    ) => Promise<boolean>;
}

export function useExportJob({
    toastId,
    messages,
}: UseExportJobParams): UseExportJobResult {
    const [activeJob, setActiveJob] = useState<ActiveExportJob | null>(null);
    const [phase, setPhase] = useState<ExportJobPhase>('idle');
    const [pollErrorCount, setPollErrorCount] = useState(0);

    const busy = phase !== 'idle';
    const polling = activeJob !== null && phase === 'processing';

    const downloadJob = useCallback(
        async (
            targetJobId: string,
            fileName: string,
            skippedCount: number,
        ): Promise<boolean> => {
            setPhase('downloading');
            try {
                const { blob, fileName: serverFileName } =
                    await EngineService.downloadExport(targetJobId);
                triggerBlobDownload(
                    blob,
                    resolveDownloadFileName(fileName, serverFileName),
                );
                if (skippedCount > 0) {
                    toast.warning(messages.readySkipped(skippedCount), {
                        id: toastId,
                    });
                } else {
                    toast.success(messages.ready, { id: toastId });
                }
                return true;
            } catch {
                toast.error(messages.failed, { id: toastId });
                return false;
            } finally {
                setPhase('idle');
                setActiveJob(null);
            }
        },
        [messages.failed, messages.ready, messages.readySkipped, toastId],
    );

    const start = useCallback(
        async (
            type: ExportType,
            data: Record<string, unknown>,
            fileName: string,
            itemCount?: number,
        ): Promise<boolean> => {
            if (busy) {
                return false;
            }
            setPhase('starting');
            setPollErrorCount(0);
            const startingMessage =
                itemCount !== undefined && messages.startingWithCount
                    ? messages.startingWithCount(itemCount)
                    : messages.starting;
            toast.loading(startingMessage, { id: toastId });
            try {
                const job = await EngineService.startExport(type, data);
                if (job.status === ExportJobStatus.COMPLETED) {
                    return await downloadJob(
                        job.id,
                        fileName,
                        job.warnings?.length ?? 0,
                    );
                }
                if (job.status === ExportJobStatus.ERROR) {
                    setPhase('idle');
                    toast.error(job.statusDetails ?? messages.failed, {
                        id: toastId,
                    });
                    return false;
                }
                setActiveJob({ id: job.id, fileName });
                setPhase('processing');
                return true;
            } catch (error) {
                setPhase('idle');
                toast.error(getApiErrorMessage(error, messages.failed), {
                    id: toastId,
                });
                return false;
            }
        },
        [
            busy,
            downloadJob,
            messages.failed,
            messages.starting,
            messages.startingWithCount,
            toastId,
        ],
    );

    const { data, error } = useSWR<ExportJob>(
        polling ? getExportJobCacheKey(activeJob.id) : null,
        () => EngineService.getExportJob(activeJob!.id),
        {
            refreshInterval: polling ? POLL_INTERVAL_MS : 0,
            revalidateOnFocus: false,
        },
    );

    useEffect(() => {
        if (phase !== 'processing' || activeJob === null) {
            return;
        }
        if (error) {
            setPollErrorCount((count) => count + 1);
            return;
        }
        if (!data) {
            return;
        }
        setPollErrorCount((count) => (count === 0 ? count : 0));
        if (data.status === ExportJobStatus.ERROR) {
            setPhase('idle');
            setActiveJob(null);
            toast.error(data.statusDetails ?? messages.failed, {
                id: toastId,
            });
            return;
        }
        if (data.status === ExportJobStatus.COMPLETED) {
            void downloadJob(
                data.id,
                activeJob.fileName,
                data.warnings?.length ?? 0,
            );
            return;
        }
        toast.loading(messages.progress(data.progress), { id: toastId });
    }, [
        data,
        error,
        phase,
        activeJob,
        downloadJob,
        messages.progress,
        messages.failed,
        toastId,
    ]);

    useEffect(() => {
        if (phase !== 'processing' || pollErrorCount < MAX_POLL_ERRORS) {
            return;
        }
        setPhase('idle');
        setActiveJob(null);
        toast.error(messages.failed, { id: toastId });
    }, [pollErrorCount, phase, messages.failed, toastId]);

    useEffect(() => {
        return () => {
            toast.dismiss(toastId);
        };
    }, [toastId]);

    return { busy, start };
}
